import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { useTestFetchingMutation, useLazyGetFormsQuery, useLazyGetProjectsQuery } from "services/core/dataFetchFromMoDa"
import { FormField } from "../interfaces/mappingInterfaces"
import {
  useLazyGetXlsFormFieldsWithGroupsQuery,
  useLazyGetAllTypesQuery,
} from "../services/marketprices/xlsFormSchemas"
import { IFormType } from "../interfaces/xlsForm"
import { seriesCombination, useGetSeriesCombinationQuery } from "../services/core/series"
import { requiredFields } from "../constants/prices"

import { ModaUploadSteps } from "../interfaces/markets"
import {
  useDeleteMSMutation,
  // useLazyGetMSExcelAsJsonQuery,
  useSaveMSDetailsMutation,
  useSaveMSExcelMutation,
} from "../services/marketprices/mappingSchemas"

const selectedOptions = requiredFields.map(field => ({
  type: field.field,
  selected: false,
}))

export interface ISchema {
  schema: Record<string, unknown>[]
  common: Record<string, unknown>
}

type CommodityByCountry = {
  commodityID: number
  commodityName: string
  commodityUnitID: number
  commodityUnitName: string
}

export interface IFormFieldList extends FormField {
  fieldIndex: number | null
  fieldMetadata?: "group" | "root" | "field"
  fieldTypeLabel?: string
  assignedTo?: string
  assignedType?: string[]
  assignedCommodity?: string
  assignedUnit?: string
  assignedCustom?: string
}

export interface IModaForm {
  apiKey?: number
  projectId?: number
  formId?: number
  mappingSchemaId?: number
  frequencyId?: number // 7
  dataCollectionFrequencyId?: number // 1
  launchAndForget?: false
  endDate?: string
  dataCollectionStartDate?: string
  dataCollectionEndDate?: string
}

type DataModaUploadContextType = {
  keyValue: number | null
  setKeyValue: Dispatch<SetStateAction<number | null>>
  modaForm: IModaForm
  setModaForm: Dispatch<SetStateAction<IModaForm>>
  keyLabel: string | null
  setKeyLabel: Dispatch<SetStateAction<string | null>>
  projectId: number | null
  setProjectId: Dispatch<SetStateAction<number | null>>
  frequencyId: string | null
  setFrequencyId: Dispatch<SetStateAction<string | null>>
  dataCollectionStartDate: string | null
  setDataCollectionStartDate: Dispatch<SetStateAction<string | null>>
  dataCollectionEndDate: string | null
  setDataCollectionEndDate: Dispatch<SetStateAction<string | null>>
  endDate: string | null
  setEndDate: Dispatch<SetStateAction<string | null>>
  projects?: { value: string; label: string }[]
  forms?: { value: string; label: string }[]
  setSelectedFormId: Dispatch<SetStateAction<number | null>>
  handleTestFetching: () => Promise<void>
  handleSaveMappingSchema: (id: number) => Promise<void>
  testModaConnection: "true" | "false" | null
  modaFormFields: IFormFieldList[]
  modaFormFieldsLoading: boolean
  modaFormFieldsError: string | null
  modaFormId: number | null
  setModaFormId: Dispatch<SetStateAction<number | null>>
  fetchModaFormFields: (xlsFormId?: number) => Promise<void>
  formTypes: Record<string, IFormType>
  country: number | null
  setCountry: Dispatch<SetStateAction<number | null>>
  checkIfNextDisabled: (section: ModaUploadSteps, subStep?: number) => boolean
  commodities?: CommodityByCountry[]
  schema: ISchema
  updateSchema: (
    index: number | null,
    value?: string,
    type?: string,
    commodity?: string,
    unit?: string,
    assignedType?: string[],
    assignedCustom?: string,
  ) => void
  selectedOptions?: typeof selectedOptions
  commonSchema: Record<string, { value: string | number; type: string }>
  setCommonSchema: Dispatch<SetStateAction<Record<string, { value: string | number; type: string }>>>
  xlsFile: File | null
  setXlsFile: Dispatch<SetStateAction<File | null>>
  xlsFormName: string | null
  setXlsFormName: Dispatch<SetStateAction<string | null>>
  schemaId: number | null
  handleSaveXlsFile: () => Promise<void>
  xlsFormId: number | null
  setXlsFormId: Dispatch<SetStateAction<number | null>>
  isLoading: boolean
  setIsFetchRequestCompleted: Dispatch<SetStateAction<boolean>>
}

const initialForm: IModaForm = {
  apiKey: undefined,
  projectId: undefined,
  formId: undefined,
  mappingSchemaId: undefined,
  frequencyId: undefined,
  dataCollectionFrequencyId: undefined,
  launchAndForget: false,
  endDate: undefined,
  dataCollectionStartDate: undefined,
  dataCollectionEndDate: undefined,
}

const DataModaUploadContext = createContext<DataModaUploadContextType | undefined>(undefined)

export const DataModaUploadProvider = ({ children }: { children: ReactNode }) => {
  const { sId } = useParams<{ sId: string }>()

  const [keyValue, setKeyValue] = useState<number | null>(null)
  const [keyLabel, setKeyLabel] = useState<string | null>(null)
  const [modaForm, setModaForm] = useState<IModaForm>(initialForm)
  const [projectId, setProjectId] = useState<number | null>(null)
  const [frequencyId, setFrequencyId] = useState<string | null>(null)
  const [dataCollectionStartDate, setDataCollectionStartDate] = useState<string | null>(null)
  const [dataCollectionEndDate, setDataCollectionEndDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [projects, setProjects] = useState<{ value: string; label: string }[]>([])

  const [xlsFile, setXlsFile] = useState<File | null>(null)
  const [xlsFormName, setXlsFormName] = useState<string | null>("")
  const [schemaId, setSchemaId] = useState<number | null>(Number(sId) || null)

  const [forms, setForms] = useState<{ value: string; label: string }[]>([])
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null)
  const [testModaConnection, setTestModaConnection] = useState<"true" | "false" | null>(null)
  const [modaFormFields, setModaFormFields] = useState<IFormFieldList[]>([])
  const [modaFormFieldsLoading, setModaFormFieldsLoading] = useState<boolean>(false)
  const [modaFormFieldsError, setModaFormFieldsError] = useState<string | null>(null)
  const [modaFormId, setModaFormId] = useState<number | null>(null)
  const [formTypes, setFormTypes] = useState<Record<string, IFormType>>({})
  const [country, setCountry] = useState<number | null>(null)
  const [commodities, setCommodities] = useState<CommodityByCountry[]>([])
  const [schema] = useState<ISchema>({ schema: [], common: {} })
  const [currentSelectedOptions, setCurrentSelectedOptions] = useState<typeof selectedOptions>(selectedOptions)
  const [commonSchema, setCommonSchema] = useState<Record<string, { value: string | number; type: string }>>({})

  const [xlsFormId, setXlsFormId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [isFetchRequestCompleted, setIsFetchRequestCompleted] = useState<boolean>(false)

  const [saveMappingSchema] = useSaveMSDetailsMutation()
  const [saveMSExcel] = useSaveMSExcelMutation()
  const [deleteMS] = useDeleteMSMutation()
  // const [getMSExcelAsJson] = useLazyGetMSExcelAsJsonQuery()

  const [getProjects] = useLazyGetProjectsQuery()
  const [getForms] = useLazyGetFormsQuery()
  const [testFetching] = useTestFetchingMutation()
  const [triggerModaFormFields] = useLazyGetXlsFormFieldsWithGroupsQuery()
  const [triggerFormTypes] = useLazyGetAllTypesQuery()

  const { data: commoditiesData, isSuccess } = useGetSeriesCombinationQuery(
    {
      admCode: country ?? 0,
      skip: 0,
      take: 0,
    },
    {
      skip: country === null,
    },
  )

  const transformModaResponse = (formFields: FormField[], types: Record<string, IFormType>): IFormFieldList[] => {
    const updatedFormFieldsList: IFormFieldList[] = []

    // 0) order by fieldRow
    const sortedFields = [...formFields].sort((a, b) => {
      if (a.fieldRow && b.fieldRow) {
        return a.fieldRow - b.fieldRow
      }
      if (a.fieldRow) return -1
      if (b.fieldRow) return 1
      return 0
    })

    // 1) check the first one of the list or if previous is different
    sortedFields
      .filter(i => i?.fieldName?.length)
      .map((field, index) => {
        updatedFormFieldsList.push({
          ...field,
          fieldIndex: index,
          fieldMetadata: "field",
          fieldTypeLabel: types[field.fieldType]?.name || "",
        })
      })

    return updatedFormFieldsList
  }

  const fetchFormTypes = async (): Promise<Record<string, IFormType>> => {
    try {
      const response = await triggerFormTypes(undefined).unwrap()
      const formattedTypes: Record<string, IFormType> = {}
      response.map(i => {
        formattedTypes[i.id] = i
      })
      setFormTypes(formattedTypes)
      return formattedTypes
    } catch (error) {
      console.error("Error fetching form types:", error)
      return {}
    }
  }

  const fetchModaFormFields = async (id?: number): Promise<void> => {
    if (!id) {
      setModaFormFields([])
      return
    }
    try {
      setModaFormFieldsLoading(true)
      setModaFormFieldsError(null)
      setCurrentSelectedOptions(
        requiredFields.map(field => ({
          type: field.field,
          selected: false,
        })),
      )
      const types = await fetchFormTypes()
      const response = await triggerModaFormFields({ xlsFormId: id }).unwrap()
      setModaFormFields(transformModaResponse(response ?? [], types || {}))
    } catch (error) {
      const err = error as { data?: string; message?: string }
      setModaFormFieldsError(typeof err?.data === "string" ? err.data : err?.message ?? "Unable to fetch Moda fields")
      setModaFormFields([])
    } finally {
      setModaFormFieldsLoading(false)
    }
  }

  const handleTestFetching = async (): Promise<void> => {
    setIsLoading(true)
    setTestModaConnection(null)
    setXlsFile(null)
    if (keyValue && selectedFormId) {
      testFetching({
        apiKeyId: keyValue,
        formId: selectedFormId,
      })
        .unwrap()
        .then(() => {
          setTestModaConnection("true")
        })
        .catch(error => {
          console.error("Error during test fetching:", error)
          setTestModaConnection("false")
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
      setTestModaConnection("false")
      console.error("Key value or selected form ID is not set")
    }
  }

  // Save the mapping schema
  const handleSaveMappingSchema = async (id: number): Promise<void> => {
    const response = await saveMappingSchema({
      adm0code: Number(country),
      mappingSchemaName: xlsFormName ?? "",
      mappingSchemaTypeId: 2, // 2 = Moda
      xlsFormId: id,
    }).unwrap()

    setSchemaId(response.id)
  }

  // Save XlsFile
  const handleSaveXlsFile = async (): Promise<void> => {
    if (!xlsFile) return
    setModaFormFields([])
    setCurrentSelectedOptions(selectedOptions)

    setIsLoading(true)
    const formData = new FormData()
    formData.append("files", xlsFile)

    await saveMSExcel(formData).unwrap()
    // const json = await getMSExcelAsJson({ fileName: result.fileName, msId: response.id }).unwrap()
    setIsLoading(false)
  }

  // SCHEMA UPDATE LOGIC
  const updateSchema = (
    index: number | null,
    value?: string,
    type?: string,
    commodity?: string,
    unit?: string,
    assignedType?: string[],
    assignedCustom?: string,
  ): void => {
    if (index === null) return
    const updatedModaFormFields = [...modaFormFields]

    // Update selected options
    const isSelected = !!value?.length
    const updatedSelectedOptions = [...currentSelectedOptions]

    if (isSelected) {
      updatedSelectedOptions.map(option => {
        if (option.type === type) {
          option.selected = true
        }
        return option
      })
    } else {
      const numberOfAssignments = updatedModaFormFields.filter(
        field =>
          field?.assignedType?.length &&
          JSON.stringify(field.assignedType || {}) === JSON.stringify(assignedType || {}),
      )?.length

      if (numberOfAssignments <= 1) {
        updatedSelectedOptions.map(option => {
          if (assignedType?.includes(option.type)) {
            option.selected = false
          }
          return option
        })
      }
    }
    setCurrentSelectedOptions(updatedSelectedOptions)

    const updatedAssignedType = () => {
      if (
        Array.isArray(updatedModaFormFields[index]?.assignedType) &&
        updatedModaFormFields[index].assignedTo === value &&
        !assignedCustom
      ) {
        !updatedModaFormFields[index]?.assignedType?.includes(type || "") &&
          updatedModaFormFields[index].assignedType?.push(type || "")
      } else {
        updatedModaFormFields[index].assignedType = [type || ""]
      }
    }

    // Update list
    isSelected ? updatedAssignedType() : (updatedModaFormFields[index].assignedType = [])
    updatedModaFormFields[index].assignedCustom = assignedCustom
    updatedModaFormFields[index].assignedCommodity = commodity
    updatedModaFormFields[index].assignedUnit = unit
    updatedModaFormFields[index].assignedTo = value
    setModaFormFields(updatedModaFormFields)
  }

  const checkIfNextDisabled = (section: ModaUploadSteps, subStep?: number): boolean => {
    if (section === ModaUploadSteps.CONFIG) {
      return country === null || !testModaConnection
    }
    if (section === ModaUploadSteps.CONNECT) {
      if (subStep === 1) {
        return xlsFormId === null || xlsFormName === null || xlsFile === null
      }
      if (subStep === 2) {
        return false
      }
      if (subStep === 3) {
        return currentSelectedOptions.some(option => !option.selected)
      }
    } else if (section === ModaUploadSteps.FINALIZE) {
      return false
    }

    return true
  }

  const beforeUnloadHandler = event => {
    event.preventDefault()
    if (!sId && !isFetchRequestCompleted && schemaId) {
      deleteMS(schemaId)
    }
    event.returnValue = true // legacy support, e.g. Chrome/Edge < 119
  }

  useEffect(() => {
    const updatedSelectedOptions = [...currentSelectedOptions]
    Object.keys(commonSchema).map(key => {
      updatedSelectedOptions.map(option => {
        if (option.type === key) {
          option.selected = commonSchema[key]?.value !== 0 && commonSchema[key]?.value?.toString()?.length > 0
        }
        return option
      })
      return null
    })
    setCurrentSelectedOptions(updatedSelectedOptions)
  }, [commonSchema])

  useEffect(() => {
    if (keyValue !== null) {
      getProjects({ apiKeyId: keyValue })
        .unwrap()
        .then(projectsData => {
          setProjects(projectsData.map((proj: any) => ({ value: proj.projectid, label: proj.name })))
        })
        .catch(error => {
          console.error("Error fetching projects:", error)
        })
    }
  }, [keyValue])

  useEffect(() => {
    if (projectId !== null) {
      // Fetch forms when projectId changes
      getForms({ apiKeyId: keyValue || 0, projectId })
        .unwrap()
        .then(formsData => {
          setForms(formsData.map((form: any) => ({ value: form.formid, label: form.title })))
        })
        .catch(error => {
          console.error("Error fetching forms:", error)
        })
    }
  }, [projectId])

  useEffect(() => {
    if (selectedFormId !== null) {
      setModaFormId(selectedFormId)
    }
    if (commoditiesData?.items?.length && isSuccess) {
      const uniqueCommodities: CommodityByCountry[] = []
      commoditiesData?.items?.reduce((acc: number[], curr) => {
        if (
          !uniqueCommodities?.some(
            item => item.commodityID === curr.commodityID && item.commodityUnitID === curr.commodityUnitID,
          )
        ) {
          acc.push(curr.commodityID)
          uniqueCommodities.push({
            commodityID: curr.commodityID,
            commodityName: curr.commodityName,
            commodityUnitID: curr.commodityUnitID,
            commodityUnitName: curr.commodityUnitName,
          })
        }
        return acc
      }, [])

      setCommodities(uniqueCommodities)
    }
  }, [selectedFormId, country, commoditiesData])

  useEffect(() => {
    setIsFetchRequestCompleted(false) // init if re-enter in the provider

    // on tab/browser close
    window.addEventListener("beforeunload", beforeUnloadHandler)

    return () => {
      if (!sId && !isFetchRequestCompleted && schemaId) {
        deleteMS(schemaId)
      }
      window.removeEventListener("beforeunload", beforeUnloadHandler)
    }
  }, [])

  const value = useMemo(
    () => ({
      keyValue,
      setKeyValue,
      modaForm,
      setModaForm,
      keyLabel,
      setKeyLabel,
      projectId,
      setProjectId,
      frequencyId,
      setFrequencyId,
      dataCollectionStartDate,
      setDataCollectionStartDate,
      dataCollectionEndDate,
      setDataCollectionEndDate,
      endDate,
      setEndDate,
      projects,
      forms,
      setSelectedFormId,
      handleTestFetching,
      handleSaveMappingSchema,
      testModaConnection,
      modaFormFields,
      modaFormFieldsLoading,
      modaFormFieldsError,
      modaFormId,
      setModaFormId,
      fetchModaFormFields,
      formTypes,
      country,
      setCountry,
      commodities,
      schema,
      updateSchema,
      selectedOptions: currentSelectedOptions,
      commonSchema,
      setCommonSchema,
      checkIfNextDisabled,
      xlsFile,
      setXlsFile,
      xlsFormName,
      setXlsFormName,
      schemaId,
      handleSaveXlsFile,
      xlsFormId,
      setXlsFormId,
      isLoading,
      setIsFetchRequestCompleted,
    }),
    [
      keyValue,
      keyLabel,
      modaForm,
      projectId,
      frequencyId,
      dataCollectionStartDate,
      dataCollectionEndDate,
      endDate,
      projects,
      forms,
      testModaConnection,
      modaFormFields,
      modaFormFieldsLoading,
      modaFormFieldsError,
      modaFormId,
      fetchModaFormFields,
      formTypes,
      country,
      commodities,
      seriesCombination,
      schema,
      currentSelectedOptions,
      commonSchema,
      xlsFile,
      xlsFormName,
      schemaId,
      xlsFormId,
      isLoading,
    ],
  )

  return <DataModaUploadContext.Provider value={value}>{children}</DataModaUploadContext.Provider>
}

export const useDataModaUploadContext = (): DataModaUploadContextType => {
  const context = useContext(DataModaUploadContext)
  if (!context) {
    throw new Error("DataModaUploadContext cannot be used outside a react Provider")
  }
  return context
}
